import React from "react";
import {useParams, useNavigate} from "react-router";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditIcon from "@mui/icons-material/Edit";
import SpaIcon from "@mui/icons-material/Spa";

import {useNewsArticle} from "./useNewsArticle";
import {formatArticleDate, calculateReadingTime, stripHtml, isSafeUrl} from "./newsArticleUtils";
import {ArticleShareBar} from "./ArticleShareBar";
import {ProseArticle} from "../app/ProseArticle";
import {SeoHead} from "../app/SeoHead";

export const ArticlePage = () => {
    const {articleId} = useParams();
    const navigate = useNavigate();

    const {
        article,
        loading,
        isNotFound,
        isDraftBlocked,
        isAdminPreview,
        relatedArticles,
    } = useNewsArticle(articleId);

    // 1. Loading Skeleton State
    if (loading) {
        return (
            <Box data-testid="article-skeleton" sx={{bgcolor: "#FAF8F5", minHeight: "85vh", py: {xs: 3, md: 5}}}>
                <Container maxWidth="md" sx={{px: {xs: 2, sm: 3}}}>
                    <Skeleton variant="text" width={180} height={36} sx={{mb: 3}} />
                    <Skeleton variant="text" width="90%" height={56} sx={{mb: 1}} />
                    <Skeleton variant="text" width="60%" height={32} sx={{mb: 3}} />
                    <Skeleton variant="rectangular" width="100%" height={320} sx={{borderRadius: 3, mb: 4}} />
                    <Skeleton variant="text" width="100%" height={24} sx={{mb: 1.5}} />
                    <Skeleton variant="text" width="100%" height={24} sx={{mb: 1.5}} />
                    <Skeleton variant="text" width="80%" height={24} sx={{mb: 3}} />
                </Container>
            </Box>
        );
    }

    // 2. Draft Blocked State (Unauthorized public visitor)
    if (isDraftBlocked) {
        return (
            <Box sx={{bgcolor: "#FAF8F5", minHeight: "85vh", py: {xs: 6, md: 10}}}>
                <Container maxWidth="sm" sx={{textAlign: "center", px: 3}}>
                    <Typography variant="h5" component="h1" sx={{fontWeight: 700, mb: 2, color: "text.primary"}}>
                        Denne artikkelen er ikke publisert ennå
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{mb: 4}}>
                        Innholdet er for øyeblikket et upublisert utkast og er ikke tilgjengelig for allmennheten.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/news")}
                        sx={{
                            borderRadius: 2,
                            minHeight: 44,
                            textTransform: "none",
                            fontWeight: 700,
                            "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                        }}
                    >
                        Tilbake til alle nyheter
                    </Button>
                </Container>
            </Box>
        );
    }

    // 3. 404 Not Found State
    if (isNotFound || !article) {
        return (
            <Box sx={{bgcolor: "#FAF8F5", minHeight: "85vh", py: {xs: 6, md: 10}}}>
                <Container maxWidth="sm" sx={{textAlign: "center", px: 3}}>
                    <Typography variant="h4" component="h1" sx={{fontWeight: 800, mb: 2, color: "text.primary"}}>
                        Artikkelen ble ikke funnet
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{mb: 4}}>
                        Saken du leter etter kan ha blitt flyttet, slettet eller er midlertidig utilgjengelig.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/news")}
                        sx={{
                            borderRadius: 2,
                            minHeight: 44,
                            textTransform: "none",
                            fontWeight: 700,
                            "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                        }}
                    >
                        Tilbake til alle nyheter
                    </Button>
                </Container>
            </Box>
        );
    }

    // 4. Successful Article Presentation
    const dateStr = formatArticleDate(article.published);
    const readingTime = calculateReadingTime(article.text);
    const metaDescription = stripHtml(article.text).slice(0, 160);
    const canonicalPath = `/nyheter/${articleId}`;

    return (
        <Box sx={{bgcolor: "#FAF8F5", minHeight: "85vh", py: {xs: 3, md: 5}}}>
            <SeoHead
                title={article.title}
                description={metaDescription}
                path={canonicalPath}
                image={article.media || undefined}
                type="article"
            />

            <Container maxWidth="md" sx={{px: {xs: 2, sm: 3}}}>
                {/* Admin Preview Notice */}
                {isAdminPreview && (
                    <Alert
                        severity="warning"
                        variant="outlined"
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => navigate("/admin/news")}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    minHeight: 44,
                                    px: 2,
                                    "&:focus-visible": {outline: "2px solid #ED6C02", outlineOffset: 2},
                                }}
                            >
                                Rediger
                            </Button>
                        }
                        sx={{
                            mb: 3,
                            borderRadius: 2,
                            fontWeight: 600,
                            bgcolor: "rgba(237, 108, 2, 0.08)",
                            borderColor: "warning.main",
                            color: "text.primary",
                        }}
                    >
                        Dette er et upublisert utkast (kun synlig for administratorer).
                    </Alert>
                )}

                {/* Back navigation */}
                <Box sx={{mb: 2.5}}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/news")}
                        sx={{
                            color: "text.secondary",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                            minHeight: 44,
                            px: 1.5,
                            "&:hover": {color: "primary.main"},
                            "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                        }}
                    >
                        Tilbake til alle nyheter
                    </Button>
                </Box>

                {/* Article Card Wrapper */}
                <Card
                    component="article"
                    elevation={0}
                    sx={{
                        borderRadius: 4,
                        border: "1px solid",
                        borderColor: "rgba(40, 82, 56, 0.12)",
                        bgcolor: "background.paper",
                        boxShadow: "0 6px 24px rgba(0, 0, 0, 0.04)",
                        overflow: "hidden",
                    }}
                >
                    {/* Media Hero Banner */}
                    {article.media ? (
                        <CardMedia
                            component="img"
                            image={article.media}
                            alt={article.title || "Artikkelbilde"}
                            loading="eager"
                            sx={{
                                width: "100%",
                                aspectRatio: "16 / 9",
                                maxHeight: {xs: 260, sm: 420},
                                objectFit: "cover",
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                height: {xs: 120, sm: 160},
                                bgcolor: "rgba(40, 82, 56, 0.05)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                overflow: "hidden",
                            }}
                        >
                            <SpaIcon sx={{fontSize: 90, color: "primary.main", opacity: 0.15}} />
                        </Box>
                    )}

                    <CardContent sx={{p: {xs: 3, sm: 5}}}>
                        {/* Title and Subtitle */}
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                fontWeight: 800,
                                color: "text.primary",
                                fontSize: {xs: "1.8rem", sm: "2.5rem"},
                                lineHeight: 1.25,
                                mb: article.subtitle ? 1.5 : 2.5,
                            }}
                        >
                            {article.title}
                        </Typography>

                        {article.subtitle && (
                            <Typography
                                variant="h6"
                                color="text.secondary"
                                sx={{
                                    fontWeight: 500,
                                    fontSize: {xs: "1.1rem", sm: "1.25rem"},
                                    lineHeight: 1.5,
                                    mb: 3,
                                }}
                            >
                                {article.subtitle}
                            </Typography>
                        )}

                        {/* Metadata row */}
                        <Stack
                            direction="row"
                            spacing={2.5}
                            flexWrap="wrap"
                            alignItems="center"
                            sx={{
                                pb: 2.5,
                                borderBottom: "1px solid",
                                borderColor: "rgba(40, 82, 56, 0.12)",
                                color: "text.secondary",
                            }}
                        >
                            {dateStr && (
                                <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                    <CalendarTodayIcon sx={{fontSize: 16, color: "primary.main"}} />
                                    <Typography variant="body2" sx={{fontWeight: 600}}>
                                        {dateStr}
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                <AccessTimeIcon sx={{fontSize: 16, color: "primary.main"}} />
                                <Typography variant="body2" sx={{fontWeight: 600}}>
                                    {readingTime}
                                </Typography>
                            </Box>

                            {article.author && (
                                <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                    <PersonIcon sx={{fontSize: 16, color: "primary.main"}} />
                                    <Typography variant="body2" sx={{fontWeight: 600}}>
                                        {article.author}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>

                        {/* Social Share Bar Top */}
                        <ArticleShareBar title={article.title} />

                        {/* Article Prose Body (Constrained column for optimal readability) */}
                        <Box sx={{maxWidth: 760, mx: "auto", my: 3}}>
                            <ProseArticle html={article.text} />
                        </Box>

                        {/* Action CTA Button Section */}
                        {article.actionText && article.actionUrl && isSafeUrl(article.actionUrl) && (
                            <Box
                                sx={{
                                    my: 4,
                                    p: {xs: 2.5, sm: 3.5},
                                    borderRadius: 3,
                                    bgcolor: "rgba(40, 82, 56, 0.05)",
                                    border: "1px solid",
                                    borderColor: "rgba(40, 82, 56, 0.15)",
                                    textAlign: "center",
                                }}
                            >
                                <Typography variant="h6" sx={{fontWeight: 700, mb: 1.5, color: "primary.main"}}>
                                    Interessert i å vite mer eller melde deg på?
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    component="a"
                                    href={article.actionUrl}
                                    target={article.actionUrl.startsWith("http") ? "_blank" : undefined}
                                    rel={article.actionUrl.startsWith("http") ? "noopener noreferrer" : undefined}
                                    onClick={(e) => {
                                        if (article.actionUrl.startsWith("/")) {
                                            e.preventDefault();
                                            navigate(article.actionUrl);
                                        }
                                    }}
                                    endIcon={
                                        article.actionUrl.startsWith("http") ? (
                                            <OpenInNewIcon fontSize="small" />
                                        ) : (
                                            <ArrowForwardIcon fontSize="small" />
                                        )
                                    }
                                    sx={{
                                        borderRadius: 2.5,
                                        fontWeight: 700,
                                        minHeight: 48,
                                        px: 3.5,
                                        fontSize: "1rem",
                                        textTransform: "none",
                                        "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                                    }}
                                >
                                    {article.actionText}
                                </Button>
                            </Box>
                        )}

                        {/* Bottom Social Share Bar */}
                        <ArticleShareBar title={article.title} />
                    </CardContent>
                </Card>

                {/* Related Articles Section */}
                {relatedArticles && relatedArticles.length > 0 && (
                    <Box sx={{mt: 6, mb: 2}}>
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{fontWeight: 800, color: "text.primary", mb: 3}}
                        >
                            Andre aktuelle saker
                        </Typography>
                        <Stack spacing={2}>
                            {relatedArticles.map((rel) => (
                                <Card
                                    key={rel.id}
                                    elevation={0}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 3,
                                        border: "1px solid",
                                        borderColor: "rgba(40, 82, 56, 0.12)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 2,
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            borderColor: "primary.main",
                                            boxShadow: "0 4px 16px rgba(40, 82, 56, 0.08)",
                                        },
                                    }}
                                    onClick={() => navigate(`/nyheter/${rel.id}`)}
                                >
                                    <Box sx={{flex: 1, minWidth: 200}}>
                                        <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                            {formatArticleDate(rel.published)}
                                        </Typography>
                                        <Typography variant="h6" sx={{fontWeight: 700, color: "text.primary"}}>
                                            {rel.title}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        endIcon={<ArrowForwardIcon />}
                                        aria-label={`Les sak: ${rel.title}`}
                                        sx={{
                                            borderRadius: 2,
                                            fontWeight: 600,
                                            textTransform: "none",
                                            minHeight: 44,
                                            "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                                        }}
                                    >
                                        Les sak
                                    </Button>
                                </Card>
                            ))}
                        </Stack>
                    </Box>
                )}
            </Container>
        </Box>
    );
};
